import { useState } from "react"

import { Heading, Overline } from "@/components/typography"
import { LogoIcon } from "@/components/LogoIcon"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group"
import { Skeleton } from "@/components/ui/skeleton"
import { getCurrencySymbol } from "@/shared/currency"
import type { CampaignType, Currency, PitchStatus, Platform, Prices } from "@/shared/types"
import { guidePriceLines } from "@/utils/prices"

import { PitchStatusBadge } from "./PitchStatusBadge"

export type PriceOrStatusCellProps = {
  status?: PitchStatus | "ineligible" | "available"
  price?: number | null
  guidePrice?: number
  currency?: Currency
  pitcherName?: string
  prices?: Prices
  platforms?: Platform[]
  campaignType?: CampaignType
  creatorLabel?: string
  onPriceChange?: (price: number | null) => void
  disabled?: boolean
  invalid?: boolean
}

export function PriceOrStatusCell({
  status = "available",
  price,
  guidePrice,
  currency = "USD",
  pitcherName,
  prices,
  platforms = [],
  campaignType = "Brand",
  creatorLabel,
  onPriceChange,
  disabled,
  invalid,
}: PriceOrStatusCellProps) {
  const [guideOpen, setGuideOpen] = useState(false)

  if (status !== "available") {
    return (
      <div>
        <PitchStatusBadge status={status} />
        {pitcherName ? (
          <Overline className="mt-1">{pitcherName}</Overline>
        ) : null}
      </div>
    )
  }

  const symbol = getCurrencySymbol(currency)
  const lines = guidePriceLines(prices, platforms, campaignType)
  const showGuide = guidePrice != null || lines.length > 0
  const displayPrice = price ?? guidePrice
  const guideDisabled = disabled || !onPriceChange

  if (!onPriceChange) {
    return (
      <div>
        <div>
          {displayPrice != null ? `${symbol}${displayPrice.toLocaleString()}` : "—"}
        </div>
        {showGuide ? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="mt-1 h-auto p-0 font-normal uppercase"
            onClick={() => setGuideOpen(true)}
          >
            <span className="text-tiny/3">Price Guide</span>
          </Button>
        ) : null}
        <PriceGuideDialog
          open={guideOpen}
          onOpenChange={setGuideOpen}
          creatorLabel={creatorLabel}
          currency={currency}
          lines={lines}
          disabled={guideDisabled}
          onSelectPrice={() => setGuideOpen(false)}
        />
      </div>
    )
  }

  return (
    <div className="w-20">
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <InputGroupText>{symbol}</InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          disabled={disabled}
          className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          value={price ?? ""}
          placeholder={
            guidePrice != null ? guidePrice.toLocaleString() : "0"
          }
          type="number"
          inputMode="numeric"
          aria-invalid={invalid || undefined}
          onChange={(event) => {
            const next = Number.parseInt(event.target.value, 10)
            onPriceChange(Number.isNaN(next) ? null : next)
          }}
          onWheel={(event) => event.currentTarget.blur()}
          aria-label="Price"
        />
      </InputGroup>
      {showGuide ? (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="mt-1 h-auto w-full p-0 font-normal uppercase"
          onClick={() => setGuideOpen(true)}
        >
          <span className="text-tiny/3">Price Guide</span>
        </Button>
      ) : null}
      <PriceGuideDialog
        open={guideOpen}
        onOpenChange={setGuideOpen}
        creatorLabel={creatorLabel}
        currency={currency}
        lines={lines}
        disabled={disabled}
        onSelectPrice={(next) => {
          onPriceChange?.(next)
          setGuideOpen(false)
        }}
      />
    </div>
  )
}

export function PriceOrStatusCellSkeleton() {
  return (
    <div className="w-20">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="mt-1 h-3 w-16" />
    </div>
  )
}

function PriceGuideDialog({
  open,
  onOpenChange,
  creatorLabel,
  currency,
  lines,
  disabled,
  onSelectPrice,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  creatorLabel?: string
  currency: Currency
  lines: ReturnType<typeof guidePriceLines>
  disabled?: boolean
  onSelectPrice: (price: number) => void
}) {
  const symbol = getCurrencySymbol(currency)
  const byPlatform = groupGuideLines(lines)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle render={<Heading type="h3" tag="h2" />}>
            Price Guide
          </DialogTitle>
          {creatorLabel ? (
            <DialogDescription>{creatorLabel}</DialogDescription>
          ) : null}
        </DialogHeader>
        {byPlatform.length === 0 ? (
          <p className="text-sm text-muted-foreground">No guide prices for this creator.</p>
        ) : (
          <div className="space-y-6">
            {byPlatform.map((group) => (
              <div key={group.platform}>
                {/* Platform icon and name sit on one label row. */}
                <div className="mb-2 flex items-center gap-2">
                  <LogoIcon brand={group.platform} className="size-5" title={group.platform} />
                  <span className="font-medium">{group.platform}</span>
                </div>
                {group.lines.map((line) => {
                  const amount = line.price && line.price > 0 ? line.price : null
                  const label = `${line.label}: ${amount ? `${symbol}${amount.toLocaleString()}` : "—"}`
                  if (disabled || !amount) {
                    return (
                      <p key={line.label} className="text-sm">
                        {label}
                      </p>
                    )
                  }
                  return (
                    <Button
                      key={line.label}
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-start px-0 py-1 font-normal"
                      onClick={() => onSelectPrice(amount)}
                    >
                      {label}
                    </Button>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function groupGuideLines(lines: ReturnType<typeof guidePriceLines>) {
  const groups: { platform: Platform; lines: typeof lines }[] = []
  for (const line of lines) {
    const existing = groups.find((group) => group.platform === line.platform)
    if (existing) {
      existing.lines.push(line)
    } else {
      groups.push({ platform: line.platform, lines: [line] })
    }
  }
  return groups
}
